import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { useSettings, waLink, telLink } from "@/lib/site";
import { BrandLogo } from "@/components/layout/BrandLogo";

const NAV = [
  { label: "Anasayfa", to: "/" },
  { label: "Paketler", to: "/paketler" },
  { label: "Galeri", to: "/galeri" },
  { label: "İlçeler", to: "/#ilceler" },
  { label: "Blog", to: "/blog" },
  { label: "İletişim", to: "/iletisim" },
];

export const Header = () => {
  const settings = useSettings();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || !isHome;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${
        solid
          ? "bg-[rgba(14,15,16,0.88)] backdrop-blur border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="container-lux flex items-center justify-between h-16 sm:h-20">
        <Link to="/" className="flex items-center" data-testid="header-logo">
          <BrandLogo settings={settings} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-lg transition-colors hover:text-white hover:bg-white/5 ${
                  isActive && n.to !== "/#ilceler" ? "text-white" : "text-[rgba(247,242,232,0.82)]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a href={telLink(settings)} data-testid="header-call-button"
            className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-sm text-[#F7F2E8] rounded-lg border border-white/15 hover:border-[rgba(200,162,74,0.55)] hover:bg-[rgba(200,162,74,0.08)] transition-colors">
            <Phone className="w-4 h-4" /> Ara
          </a>
          <a href={waLink(settings)} target="_blank" rel="noreferrer" data-testid="header-whatsapp-button">
            <Button className="bg-[#C8A24A] text-[#141517] hover:bg-[#D7B86A] font-semibold h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-4" aria-label="WhatsApp">
              <MessageCircle className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">WhatsApp</span>
            </Button>
          </a>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden text-[#F7F2E8] hover:bg-white/10"
                data-testid="mobile-menu-button" aria-label="Menü">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#141517] border-white/10 text-[#F7F2E8]">
              <div className="mt-8 flex flex-col gap-1">
                {NAV.map((n) => (
                  <SheetClose asChild key={n.to}>
                    <Link to={n.to} className="px-3 py-3 rounded-lg text-lg hover:bg-white/5">
                      {n.label}
                    </Link>
                  </SheetClose>
                ))}
                <div className="h-px bg-white/10 my-4" />
                <a href={waLink(settings)} target="_blank" rel="noreferrer">
                  <Button className="w-full bg-[#C8A24A] text-[#141517] hover:bg-[#D7B86A] font-semibold">
                    <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp'tan Yaz
                  </Button>
                </a>
                <a href={telLink(settings)}>
                  <Button variant="outline" className="w-full mt-2 border-white/20 text-[#F7F2E8] hover:bg-white/5">
                    <Phone className="w-4 h-4 mr-1.5" /> Hemen Ara
                  </Button>
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
